'use client';

import { useEffect, useRef } from 'react';
import { CampaignLink } from '@/components/CampaignLink';
import type { TownLandingLink } from '@/models/Product';

const MARQUEE_MIN_TOWNS = 10;

function TownChip({
  town,
  ariaHidden,
}: {
  town: TownLandingLink;
  ariaHidden?: boolean;
}) {
  return (
    <CampaignLink
      href={town.url}
      className="flex flex-row items-center gap-2 shrink-0 bg-hzc_beige_100 hover:bg-hzc_beige_200 transition-colors rounded-xl px-3 py-2"
      aria-hidden={ariaHidden}
    >
      <span className="text-[12px] text-hzc_purple_500 font-medium whitespace-nowrap">
        {town.title}
      </span>
    </CampaignLink>
  );
}

export function TownStoryStrip({ towns }: { towns: TownLandingLink[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isPausedRef = useRef(false);
  const animFrameRef = useRef<number | null>(null);
  const speedRef = useRef(0.75);
  const scrollLeftRef = useRef(0);
  const isProgrammaticScrollRef = useRef(false);
  const shouldMarquee = towns.length >= MARQUEE_MIN_TOWNS;

  useEffect(() => {
    if (!shouldMarquee) return;

    const el = scrollRef.current;
    if (!el) return;

    const halfWidth = el.scrollWidth / 2;
    scrollLeftRef.current = el.scrollLeft;

    const syncScrollLeft = () => {
      if (isProgrammaticScrollRef.current) {
        isProgrammaticScrollRef.current = false;
        return;
      }

      scrollLeftRef.current = el.scrollLeft;
    };

    el.addEventListener('scroll', syncScrollLeft, { passive: true });

    const tick = () => {
      if (!isPausedRef.current && el) {
        scrollLeftRef.current += speedRef.current;

        if (scrollLeftRef.current >= halfWidth) {
          scrollLeftRef.current = 0;
        }

        isProgrammaticScrollRef.current = true;
        el.scrollLeft = scrollLeftRef.current;
      }
      animFrameRef.current = requestAnimationFrame(tick);
    };

    animFrameRef.current = requestAnimationFrame(tick);

    return () => {
      el.removeEventListener('scroll', syncScrollLeft);
      if (animFrameRef.current !== null) cancelAnimationFrame(animFrameRef.current);
    };
  }, [towns, shouldMarquee]);

  if (!towns.length) return null;

  const items = shouldMarquee ? [...towns, ...towns] : towns;

  return (
    <div
      className="relative w-full py-1 overflow-hidden min-h-[48px]"
      onMouseEnter={() => {
        if (shouldMarquee) isPausedRef.current = true;
      }}
      onMouseLeave={() => {
        if (shouldMarquee) isPausedRef.current = false;
      }}
    >
      <div
        ref={shouldMarquee ? scrollRef : undefined}
        className="overflow-x-auto overflow-y-hidden flex gap-1 px-3 py-3 scrollbar-hide overscroll-x-contain justify-start"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {items.map((town, i) => (
          <TownChip
            key={shouldMarquee ? `${town.id}-${i}` : town.id}
            town={town}
            ariaHidden={shouldMarquee && i >= towns.length}
          />
        ))}
      </div>
    </div>
  );
}
